(ns app.front.view.group.reporting.table
  (:require [app.domain.address.model :as address-model]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.href :as href]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [reagent.core :as r]))


(defn- format-address [structured-address]
  (->> (address-model/local-address-attributes :CA-ON)
       (keep (fn [k]
               (get structured-address k)))
       (str/join " ")))


(defn error-indicator [tr {:assessment-report/keys [integration-error]}]
  (r/with-let [*hover? (r/atom false)]
    [:div.relative {:on-mouse-enter #(reset! *hover? true)
                    :on-mouse-leave #(reset! *hover? false)}
     (cc/icon {:icon        i/circle-attention
               :fill        "#f71735"
               :description (tr [:assessment-report/integration-error])})
     (when @*hover?
       (cc/toast-notification {:kind              :error
                               :title             (tr [:assessment-reporting/error-desc])
                               :hide-close-button true
                               :caption           integration-error}))]))


(defn content-cell [tr v process-fn error error-text]
  [:div.w-full
   (if (or (nil? v)
           (and (coll? v)
                (empty? v))
           (and (string? v)
                (str/blank? v)))
     (or error-text "-")
     (let [value      (-> (process-fn v) str)
           long?      (> (count value) 22)
           short-text (if long?
                        (str (->> (take 20 value) (apply str)) "…")
                        value)]
       [:div.relative
        [:span short-text]
        (when error
          [:<>
           [:br]
           [:span (str (tr [:error/error]) ": " error-text)]])
        (when long?
          [:div.hover-long-text
           value])]))])


(defn report-row [tr
                  {:assessment-report/keys [integration-state integration-error reported permit-state]
                   :db/keys                [id]                                                        :as report}
                  {:assessment-report/keys [city municipal-permit-number postal-code
                                            province roll-number time-zone work-type-description currency
                                            cloudpermit-id]
                   :keys                   [errors]
                   :as                     permit-data}
                  *selected-reports]
  (letfn [(content-or-dash
            ([k process-fn]
             (content-or-dash k process-fn {}))
            ([k process-fn opts]
             (let [v          (get permit-data k)
                   error      (get errors k)
                   error-text (cond
                                (= error 'missing-required-key) (tr [:schema-error/missing-required-key])
                                error (str error))]
               {:content [content-cell tr v process-fn error error-text]
                :opts    (p/deep-merge :into
                                       {:class (futil/cs (when error "invalid"))}
                                       opts)})))]
    {:key  (str id)
     :cols (concat
             [{:content (cond
                          (= integration-state :assessment-report.integration-state/in-progress)
                          (cc/inline-loading {:success (= integration-state :assessment-report.integration-state/complete)})

                          (= integration-state :assessment-report.integration-state/complete)
                          (cc/icon {:icon        i/check
                                    :fill        "green"
                                    :description (tr [:integration-state/complete])})


                          (some? *selected-reports)
                          (cc/checkbox {:id         (str id "-select")
                                        :label-text (tr [:select/select])
                                        :hide-label true
                                        :checked    (contains? @*selected-reports id)
                                        :on-change  #(swap! *selected-reports (if % conj disj) id)}))}
              {:content (cond
                          integration-error
                          [error-indicator tr report]

                          (seq errors)
                          (cc/icon {:icon        i/circle-attention
                                    :description (tr [:error/invalid-value])
                                    :fill        "#f71735"}))
               :opts    {:style {:padding "0.875rem 0"}}}
              {:content (tr [permit-state])}]
             (if permit-data
               (concat [{:content (cc/link {:href (href/href :view/workspace {:workspace-id cloudpermit-id})}
                                           municipal-permit-number)
                         :opts    {:style {:min-width "175px"}}}
                        {:content roll-number
                         :opts    {:style {:min-width "150px"}}}
                        (content-or-dash :assessment-report/address
                                         #(str (format-address %)
                                               " " postal-code " " city ", " province)
                                         {:style {:min-width "190px"}})
                        (content-or-dash :assessment-report/permit-value
                                         #(futil/money-string (* 100 %) currency))
                        (content-or-dash :assessment-report/statcan-work-type
                                         #(tr [(keyword "statcan-work-type" %)])
                                         {:style {:min-width "170px"}})
                        (content-or-dash :assessment-report/statcan-building-type
                                         #(tr [(keyword "statcan-building-type" %)])
                                         {:style {:min-width "185px"}})
                        {:content work-type-description}
                        (content-or-dash :assessment-report/gross-floor-area-m2 #(str % " m²"))]
                       (->> [[:assessment-report/units-created]
                             [:assessment-report/description-of-work {:min-width "180px"}]
                             [:assessment-report/proposed-use-of-building {:min-width "170px"}]
                             [:assessment-report/current-use-of-building {:min-width "170px"}]
                             [:assessment-report/legal-description {:min-width "170px"}]
                             [:assessment-report/zoning-types]
                             [:assessment-report/builder-name {:min-width "170px"}]
                             [:assessment-report/property-owner-name {:min-width "170px"}]
                             [:assessment-report/tarion-enrol-number]]
                            (mapv (fn [[k style]]
                                    (content-or-dash k
                                                     (if (= k :assessment-report/zoning-types)
                                                       #(str/join ", " %)
                                                       identity)
                                                     (p/assoc-if {} :style style)))))
                       [{:content (or (date-util/date-in-tz reported time-zone) "-")}]
                       (->> [:assessment-report/received-date
                             :assessment-report/issued-date
                             :assessment-report/occupancy-date
                             :assessment-report/final-date
                             :assessment-report/cancelled-date
                             :assessment-report/expiry-date]
                            (mapv (fn [k] (content-or-dash k #(date-util/date-in-tz % time-zone))))))
               [{:content (cc/skeleton-text {:width "100%"})
                 :opts    {:col-span 24}}]))}))


(defn report-sort [[k descending?] permit-data items]
  (if k
    (let [compare-fn (if descending? #(compare %2 %1) compare)
          key-fn     (fn [report]
                       (if (= k :assessment-report/address)
                         (->> (:db/id report) (get permit-data) k format-address)
                         (or (-> report k)
                             (->> (:db/id report) (get permit-data) k))))]
      (sort-by key-fn compare-fn items))
    items))


(defn report-table [id tr reports permit-data *selected-reports]
  (r/with-let [*sorting (r/atom [])]
    [eb/error-boundary
     [:div.w-full.overflow-x-scroll.overflow-y-visible.pb-12
      #_{:clj-kondo/ignore [:deprecated-var]}
      [ui-table/table {:tr    tr
                       :class "admin-reporting__report-table"}
       (concat [(if *selected-reports
                  [ui-table/table-header-checkbox
                   (str id "-select-all")
                   (tr [:select/all])
                   (fn [selected?]
                     (reset! *selected-reports (if selected?
                                                 (->> reports
                                                      (filter #(-> % :errors empty?))
                                                      (mapv :db/id)
                                                      set)
                                                 #{})))]
                  [:th])
                [:th {:style {:padding "0"}}]]
               (->> [:assessment-report/permit-state
                     :assessment-report/municipal-permit-number
                     ;; While this is only used in Ontario, referring to "roll number" is safe
                     :assessment-report/roll-number
                     :assessment-report/address
                     :assessment-report/permit-value
                     :assessment-report/statcan-work-type
                     :assessment-report/statcan-building-type
                     :assessment-report/work-type-description
                     :assessment-report/gross-floor-area-m2
                     :assessment-report/units-created
                     :assessment-report/description-of-work
                     :assessment-report/proposed-use-of-building
                     :assessment-report/current-use-of-building
                     :assessment-report/legal-description
                     :assessment-report/zoning-types
                     :assessment-report/builder-name
                     :assessment-report/property-owner-name
                     :assessment-report/tarion-enrol-number
                     :assessment-report/reported
                     :assessment-report/received-date
                     :assessment-report/issued-date
                     :assessment-report/occupancy-date
                     :assessment-report/final-date
                     :assessment-report/cancelled-date
                     :assessment-report/expiry-date]
                    (mapv (fn [k] [ui-table/table-header-sortable tr *sorting k]))))
       (->> reports
            (report-sort @*sorting permit-data)
            (mapv (fn [{:db/keys [id] :as report}]
                    (report-row tr report (get permit-data id) *selected-reports))))]]]))
